import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Grid,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    IconButton,
    Chip,
    Divider
} from '@mui/material';
import {
    AddIcon,
    EditOutlinedIcon,
    DeleteOutlineIcon,
    LinkOutlinedIcon,
    EmailOutlinedIcon,
    CallIcon,
    LanguageIcon,
    LinkedInIcon
} from 'icons/materialIcons';
import { TeamV2ContactLinkItem, TeamV2ContactLinkConfig } from '../contactLinks';
import { generateMockTeamV2ContactLinks } from '../../utils/teamV2ContactLinkUtils';

const TeamV2MemberContactLinksContainer = ({ memberData, setMemberData }) => {
    const [contactLinks, setContactLinks] = useState([]);
    const [contactLinkModal, setContactLinkModal] = useState({
        open: false,
        contactLink: null,
        isEditing: false
    });

    // Initialize with mock data
    useEffect(() => {
        if (memberData.contactLinks && memberData.contactLinks.length > 0) {
            setContactLinks(memberData.contactLinks);
        } else {
            // Generate mock contact links for new members
            const mockLinks = generateMockTeamV2ContactLinks(memberData.id, 4);
            setContactLinks(mockLinks);
        }
    }, [memberData.id]);

    const handleAddContactLink = () => {
        setContactLinkModal({
            open: true,
            contactLink: null,
            isEditing: false
        });
    };

    const handleEditContactLink = (contactLink) => {
        setContactLinkModal({
            open: true,
            contactLink: contactLink,
            isEditing: true
        });
    };

    const handleDeleteContactLink = (contactLinkId) => {
        const updatedLinks = contactLinks.filter(link => link.id !== contactLinkId);
        setContactLinks(updatedLinks);
        updateMemberData(updatedLinks);
    };

    const handleContactLinkSave = (contactLinkData) => {
        if (contactLinkModal.isEditing) {
            // Update existing contact link
            const updatedLinks = contactLinks.map(link =>
                link.id === contactLinkData.id ? contactLinkData : link
            );
            setContactLinks(updatedLinks);
            updateMemberData(updatedLinks);
        } else {
            // Add new contact link
            const newLink = {
                ...contactLinkData,
                id: `link-${Date.now()}`,
                teamMemberId: memberData.id
            };
            const updatedLinks = [...contactLinks, newLink];
            setContactLinks(updatedLinks);
            updateMemberData(updatedLinks);
        }

        setContactLinkModal({
            open: false,
            contactLink: null,
            isEditing: false
        });
    };

    const handleContactLinkModalClose = () => {
        setContactLinkModal({
            open: false,
            contactLink: null,
            isEditing: false
        });
    };

    const updateMemberData = (updatedLinks) => {
        setMemberData(prev => ({
            ...prev,
            contactLinks: updatedLinks
        }));
    };

    const getContactLinkIcon = (type) => {
        const iconMap = {
            email: <EmailOutlinedIcon />,
            phone: <CallIcon />,
            website: <LanguageIcon />,
            linkedin: <LinkedInIcon />
        };
        return iconMap[type] || <LinkOutlinedIcon />;
    };

    const getContactLinkColor = (type) => {
        const colorMap = {
            email: '#EA4335',
            phone: '#34A853',
            website: '#4285F4',
            linkedin: '#0077B5'
        };
        return colorMap[type] || '#666666';
    };

    return (
        <Box sx={{ padding: '2rem' }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '2rem'
            }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#333' }}>
                    Contact Links
                </Typography>

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddContactLink}
                    sx={{
                        backgroundColor: '#000',
                        textTransform: 'none',
                        '&:hover': { backgroundColor: '#333' }
                    }}
                >
                    Add Contact Link
                </Button>
            </Box>

            {/* Contact Links List */}
            <Paper sx={{ padding: '1.5rem' }}>
                <Typography variant="h6" sx={{
                    fontWeight: 600,
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <LinkOutlinedIcon />
                    Member Contact Information
                </Typography>

                {contactLinks.length === 0 ? (
                    <Box sx={{
                        textAlign: 'center',
                        padding: '3rem',
                        color: '#999'
                    }}>
                        <LinkOutlinedIcon sx={{ fontSize: 48, marginBottom: 1, opacity: 0.5 }} />
                        <Typography variant="body1" sx={{ marginBottom: 1 }}>
                            No contact links added yet
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                            Add contact links to help team members connect
                        </Typography>
                    </Box>
                ) : (
                    <List sx={{ '& .MuiListItem-root': { padding: '8px 0' } }}>
                        {contactLinks.map((link, index) => (
                            <React.Fragment key={link.id}>
                                <ListItem sx={{
                                    padding: '12px 0',
                                    '&:hover': { backgroundColor: '#f5f5f5' }
                                }}>
                                    <ListItemIcon sx={{
                                        minWidth: '40px',
                                        color: getContactLinkColor(link.type)
                                    }}>
                                        {getContactLinkIcon(link.type)}
                                    </ListItemIcon>

                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                    {link.title}
                                                </Typography>
                                                <Chip
                                                    label={link.type.charAt(0).toUpperCase() + link.type.slice(1)}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: getContactLinkColor(link.type),
                                                        color: 'white',
                                                        fontSize: '0.7rem',
                                                        height: '20px'
                                                    }}
                                                />
                                                {!link.isActive && (
                                                    <Chip
                                                        label="Inactive"
                                                        size="small"
                                                        color="warning"
                                                        sx={{ fontSize: '0.7rem', height: '20px' }}
                                                    />
                                                )}
                                            </Box>
                                        }
                                        secondary={
                                            <Typography variant="body2" sx={{ color: '#666' }}>
                                                {link.url}
                                            </Typography>
                                        }
                                    />

                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEditContactLink(link)}
                                            sx={{ color: '#666' }}
                                        >
                                            <EditOutlinedIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDeleteContactLink(link.id)}
                                            sx={{ color: '#f44336' }}
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </ListItem>
                                {index < contactLinks.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                    </List>
                )}
            </Paper>

            {/* Quick Add Buttons */}
            <Paper sx={{ padding: '1.5rem', marginTop: '1.5rem' }}>
                <Typography variant="h6" sx={{
                    fontWeight: 600,
                    marginBottom: '1rem'
                }}>
                    Quick Add
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <TeamV2ContactLinkItem
                            type="email"
                            onClick={() => {
                                setContactLinkModal({
                                    open: true,
                                    contactLink: { type: 'email', title: 'Email' },
                                    isEditing: false
                                });
                            }}
                            size="medium"
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TeamV2ContactLinkItem
                            type="phone"
                            onClick={() => {
                                setContactLinkModal({
                                    open: true,
                                    contactLink: { type: 'phone', title: 'Phone' },
                                    isEditing: false
                                });
                            }}
                            size="medium"
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TeamV2ContactLinkItem
                            type="website"
                            onClick={() => {
                                setContactLinkModal({
                                    open: true,
                                    contactLink: { type: 'website', title: 'Website' },
                                    isEditing: false
                                });
                            }}
                            size="medium"
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TeamV2ContactLinkItem
                            type="linkedin"
                            onClick={() => {
                                setContactLinkModal({
                                    open: true,
                                    contactLink: { type: 'linkedin', title: 'LinkedIn' },
                                    isEditing: false
                                });
                            }}
                            size="medium"
                            variant="outlined"
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* Contact Link Configuration Modal */}
            <TeamV2ContactLinkConfig
                open={contactLinkModal.open}
                onClose={handleContactLinkModalClose}
                onSave={handleContactLinkSave}
                contactLink={contactLinkModal.contactLink}
                teamMemberId={memberData.id}
            />
        </Box>
    );
};

export default TeamV2MemberContactLinksContainer;
